import { Link } from 'react-router-dom'
import type { Archive } from '@/types'
import { SmartImage } from './SmartImage'

interface ArchiveGridProps {
  archives: Archive[]
}

const archiveStyles: Record<string, { gradient: string; border: string }> = {
  treaty: {
    gradient: 'from-blue-900/20 to-transparent',
    border: 'border-blue-500/20 hover:border-blue-500/40',
  },
  conflict: {
    gradient: 'from-red-900/20 to-transparent',
    border: 'border-red-500/20 hover:border-red-500/40',
  },
  civilization: {
    gradient: 'from-amber-900/20 to-transparent',
    border: 'border-amber-500/20 hover:border-amber-500/40',
  },
  origin: {
    gradient: 'from-emerald-900/20 to-transparent',
    border: 'border-emerald-500/20 hover:border-emerald-500/40',
  },
  lineage: {
    gradient: 'from-purple-900/20 to-transparent',
    border: 'border-purple-500/20 hover:border-purple-500/40',
  },
  classified: {
    gradient: 'from-slate-900/20 to-transparent',
    border: 'border-slate-500/20 hover:border-slate-500/40',
  },
  exploration: {
    gradient: 'from-cyan-900/20 to-transparent',
    border: 'border-cyan-500/20 hover:border-cyan-500/40',
  },
  mythology: {
    gradient: 'from-indigo-900/20 to-transparent',
    border: 'border-indigo-500/20 hover:border-indigo-500/40',
  },
}

export default function ArchiveGrid({ archives }: ArchiveGridProps) {
  const renderArchiveCard = (archive: Archive) => {
    const style = archiveStyles[archive.key] || {
      gradient: 'from-archive-stone/20 to-transparent',
      border: 'border-archive-stone/20 hover:border-archive-stone/40',
    }

    return (
      <Link
        key={archive.key}
        to={`/archive/${archive.key}`}
        className={`group relative artifact-frame ${style.border} overflow-hidden`}
      >
        {/* Background gradient */}
        <div className={`absolute inset-0 bg-gradient-to-b ${style.gradient} opacity-50`} />

        {/* Content */}
        <div className="relative p-6 text-center">
          {/* Seal */}
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-archive-slate overflow-hidden border-2 border-archive-ash/50 group-hover:border-archive-gold/50 transition-colors">
            <SmartImage
              src={archive.seal_image_url || `/images/archives/${archive.key}.png`}
              alt={`${archive.name} seal`}
              sizes="76px"
              width={76}
              height={76}
              loading="lazy"
              className="medallion w-full h-full object-cover"
            />
          </div>

          {/* Name */}
          <h3 className="font-display text-archive-cream text-lg group-hover:text-archive-gold transition-colors">
            {archive.name}
          </h3>

          {/* Description */}
          <p className="mt-2 text-sm text-archive-muted line-clamp-2">
            {archive.description}
          </p>

          {/* Arrow indicator */}
          <div className="mt-4 flex justify-center">
            <span className="inline-flex items-center gap-1 text-xs text-archive-gold opacity-0 group-hover:opacity-100 transition-opacity">
              Enter Archive
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {archives.map(renderArchiveCard)}
    </div>
  )
}
